// components/PropertyDashboard.js
import React, { useEffect, useState } from "react";

import { AiOutlineClose, AiOutlineArrowRight } from "react-icons/ai";
import { toast } from "react-toastify";

import { baseUrl } from "../../../Environments/environment";
import { useAuth } from "../../../Contexts/AuthContext/AuthContext";
import LoadingSpinner from "../../LoadingSpinner/LoadingSpinner";
import { useProperty } from "../../../Contexts/PropertyContext/PropertyContext";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../../../api";
import { Trash2Icon, TrashIcon } from "lucide-react";

// function classNames(...classes) {
//   return classes.filter(Boolean).join(" ");
// }

const PropertyDashboard = () => {
	const [properties, setProperties] = useState([]);
	const { accessToken, user, logout } = useAuth();
	const { selectProperty } = useProperty();
	const [loading, setLoading] = useState(false);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [selectedProperty, setSelectedProperty] = useState(null);
	const navigate = useNavigate(); // Initialize useNavigate
	useEffect(() => {
		const fetchProperties = async () => {
			setLoading(true);

			try {
				const response = await apiClient.get(`${baseUrl}/property`);
				const fetchedProperties = response.data.result.properties || [];

				if (fetchedProperties.length === 0) {
					navigate("/addproperty", { state: { from: "propertyhome" } });
					return;
				}

				if (fetchedProperties.length === 1) {
					selectProperty(fetchedProperties[0]._id);
					navigate("/dashboard");
					return;
				}

				setProperties(fetchedProperties);
				const propertiesList = JSON.stringify(fetchedProperties);
				localStorage.setItem("properties", propertiesList);
			} catch (error) {
				const errorMessage =
					error.response?.data?.message ||
					"Failed to fetch properties. Please try again.";
				toast.error(errorMessage);
				// console.error("Error fetching properties:", errorMessage);
			} finally {
				setLoading(false);
			}
		};

		fetchProperties();
	}, [accessToken]);

	// const handleCardClick = (property) => {
	//   selectProperty(property._id);
	//   navigate("/dashboard");
	// };

	const handleCardClick = async (property) => {
		const { _id: propertyId } = property;
		try {
			selectProperty(propertyId);
			navigate("/dashboard");
		} catch (error) {
			const errorMessage =
				error.response?.data?.message || "Error checking Twilio setup:";
			toast.error(errorMessage);
			// Handle error (e.g., show a notification)
		}
	};

	const handleLogout = async () => {
		try {
			await logout();
			navigate("/"); // Navigate to homepage after logout
		} catch (error) {
			console.error("Failed to log out:", error);
		}
	};

	const openModal = (property) => {
		setSelectedProperty(property);
		setIsModalOpen(true);
	};

	const closeModal = () => {
		setSelectedProperty(null);
		setIsModalOpen(false);
	};
	const deleteProperty = async (e, propertyId) => {
		e.stopPropagation(); // Prevent triggering card click
		try {
			setLoading(true);
			const response = await apiClient.delete(
				`${baseUrl}/property/${propertyId}`,
			);
			const updatedProperties = properties.filter(
				(property) => property._id !== propertyId,
			);
			setProperties(updatedProperties);
			localStorage.setItem("properties", JSON.stringify(updatedProperties));
			toast.success("Property deleted successfully");
		} catch (error) {
			const errorMessage =
				error.response?.data?.message || "Failed to delete property.";
			toast.error(errorMessage);
		} finally {
			setLoading(false);
		}
	};

	return (
		<>
			{loading && <LoadingSpinner />}
			<div className="min-h-screen bg-gray-300 px-4 py-6 sm:px-6 lg:px-8">
				<div className="mx-auto w-full max-w-7xl">
					<div className="mb-6 flex flex-col gap-4 rounded-xl bg-white/70 p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
						<div className="flex items-center gap-4">
							<h2 className="text-lg font-semibold text-gray-800 sm:text-xl">
								Welcome Again!
							</h2>
						</div>
						<button
							onClick={handleLogout}
							className="w-full rounded-md bg-red-500 px-4 py-2 text-white hover:bg-red-600 sm:w-auto"
						>
							Logout
						</button>
					</div>

					<div className="rounded-xl bg-white p-4 shadow-sm sm:p-6">
						<div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
							<div>
								<h3 className="text-lg font-semibold leading-6 text-gray-900">
									Properties assigned to you
								</h3>
								<p className="mt-2 text-sm text-gray-600">
									Select one to proceed
								</p>
							</div>
							{user?.role === "admin" && (
								<button
									type="button"
									onClick={() =>
										navigate("/addproperty", {
											state: { from: "propertyhome" },
										})
									}
									className="bg-indigo-600 text-white px-4 py-2 rounded-md shadow hover:bg-indigo-700"
								>
									Onboard New Property
								</button>
							)}
						</div>

						<dl className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
							{properties.filter(Boolean).map((property) => (
								<div
									key={property._id}
									className="relative mx-auto flex w-full max-w-full flex-col overflow-hidden rounded-lg bg-white shadow-lg sm:max-w-sm"
								>
									<div
										className="flex-1 p-4 cursor-pointer"
										onClick={() => handleCardClick(property)}
									>
										<div className="flex items-center">
											<img
												src={property.logoUrl}
												alt={`${property.name} logo`}
												className="h-10 w-10 rounded-full border-2 border-indigo-500 "
											/>
											<div className="ml-4 flex-1">
												<h3 className="flex items-center justify-between text-md font-semibold text-gray-900 cursor-pointer">
													{property.name}
													{property.deleteFlag === true && (
														<Trash2Icon
															className="text-red-500 hover:text-red-700 cursor-pointer w-5 h-5"
															onClick={(e) => deleteProperty(e, property._id)}
														/>
													)}
												</h3>
												<p className="text-xs text-gray-500">
													Country: {property.country}
												</p>
												<p className="text-xs text-gray-500 truncate">
													Address: {property.address}
												</p>
											</div>
										</div>
									</div>

									{/* Actions */}
									<div className="bg-gray-50 px-4 py-4 flex justify-between items-center">
										<button
											onClick={() => openModal(property)}
											className="text-sm font-medium text-indigo-600 hover:text-indigo-500"
										>
											View Full Details
										</button>
										<div
											// href={`mailto:${property.email}`}  {/* Contact via email */}
											className="text-sm font-medium text-indigo-600 hover:text-indigo-500"
										>
											Add User
										</div>
										{/* Arrow button new code change */}
										{/* <button
											onClick={() => handleCardClick(property)}
											className="text-indigo-600 hover:text-indigo-500 transition-transform transform hover:scale-110"
										>
											<AiOutlineArrowRight size={20} />
										</button> */}
									</div>
								</div>
							))}
						</dl>
					</div>
				</div>

				{isModalOpen && selectedProperty && (
					<div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
						<div className="bg-white p-6 sm:p-8 rounded-lg shadow-xl w-[90vw] sm:w-[60vw] lg:w-[30vw] overflow-y-auto  h-[90vh]">
							<div className="flex justify-between items-center mb-6">
								<h2 className="text-xl font-semibold text-gray-900">
									Property Details
								</h2>
								<button
									onClick={closeModal}
									className="text-gray-600 hover:text-gray-900 focus:outline-none"
								>
									<AiOutlineClose size={24} />
								</button>
							</div>
							<div className="space-y-6">
								<div className="space-y-1">
									<label className="block text-sm font-medium text-gray-700">
										Name
									</label>
									<p className="text-base text-gray-800">
										{selectedProperty.name}
									</p>
								</div>
								<div className="space-y-1">
									<label className="block text-sm font-medium text-gray-700">
										Email
									</label>
									<p className="text-base text-gray-800">
										{selectedProperty.email}
									</p>
								</div>
								<div className="space-y-1">
									<label className="block text-sm font-medium text-gray-700">
										Phone Number
									</label>
									<p className="text-base text-gray-800">
										{selectedProperty.countryCode}
										{selectedProperty.phoneNumber}
									</p>
								</div>
								<div className="space-y-1">
									<label className="block text-sm font-medium text-gray-700">
										Website
									</label>
									<p className="text-base text-gray-800">
										{selectedProperty.website}
									</p>
								</div>
								<div className="space-y-1">
									<label className="block text-sm font-medium text-gray-700">
										About
									</label>
									<p className="text-base text-gray-800">
										{selectedProperty.about}
									</p>
								</div>
								<div className="space-y-1">
									<label className="block text-sm font-medium text-gray-700">
										Country
									</label>
									<p className="text-base text-gray-800">
										{selectedProperty.country}
									</p>
								</div>
								<div className="space-y-1">
									<label className="block text-sm font-medium text-gray-700">
										State
									</label>
									<p className="text-base text-gray-800">
										{selectedProperty.state}
									</p>
								</div>
								<div className="space-y-1">
									<label className="block text-sm font-medium text-gray-700">
										City
									</label>
									<p className="text-base text-gray-800">
										{selectedProperty.city}
									</p>
								</div>
								<div className="space-y-1">
									<label className="block text-sm font-medium text-gray-700">
										Address
									</label>
									<p className="text-base text-gray-800">
										{selectedProperty.address}
									</p>
								</div>

								<div className="space-y-1">
									<label className="block text-sm font-medium text-gray-700">
										Active
									</label>
									<p className="text-base text-gray-800">
										{selectedProperty.active ? "Yes" : "No"}
									</p>
								</div>
								<div className="space-y-1">
									<label className="block text-sm font-medium text-gray-700">
										Created At
									</label>
									<p className="text-base text-gray-800">
										{new Date(selectedProperty.createdAt).toLocaleString()}
									</p>
								</div>
								<div className="space-y-1">
									<label className="block text-sm font-medium text-gray-700">
										Updated At
									</label>
									<p className="text-base text-gray-800">
										{new Date(selectedProperty.updatedAt).toLocaleString()}
									</p>
								</div>
								{/* Add more property details here */}
							</div>
						</div>
					</div>
				)}
			</div>
		</>
	);
};

export default PropertyDashboard;
